import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useToggle } from 'react-use';

import { useAuth } from '../../hooks/useAuth';
import { useActions } from '../../hooks/useActions';
import PostHeader from './PostHeader/PostHeader';
import PostBody from './PostBody/PostBody';
import PostFooter from './PostFooter/PostFooter';
import ConfirmationModal from './../elements/modal/NewConfirmModal';

import { createReview } from '../../firebase/api/review';
import { getPostByUid, markPostAsUnread, updatePost } from '../../firebase/api/post';
import { getUserByUid } from '../../firebase/api/users';
import { sendGridEmail } from '../../firebase/api/mail';
import {
    getAcceptPost,
    getCompleteProject,
    getDeclinePost,
    getFirstAnswerForInvite,
    getInvoice,
    getEmailsForHiredAfterDeletePost,
    getEmailsForInvitedAfterDeletePost,
} from '../../templates';
import { convertDateToFirestoreTimestamp } from '../../firebase/api/firestoreUtils';
import { responsePendingTypes } from './PostFooter/PendingPostLabel/PendingPostLabel';

import './Post.css';
import { SidekickCategories } from '../../static/categories';
import { useLocation, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PublishedProposals from './PublishedProposals/PublishedProposals';
import { postsPages } from '../projectSubCategory/SubCategory';
import _cloneDeep from 'lodash/cloneDeep';
import * as ROUTES from '../../constants/routes';
import { ReactComponent as DeleteIcon } from './delete-mark.svg';
import AddPortfolioToInvitePopup from '../elements/addPortfolioToInvitePopup/AddPortfolioToInvitePopup';
import { useSelector } from '../../hooks/useSelector';

export const postStatuses = {
    new: 'new',
    inProgress: 'in progress',
    requestInvoice: 'request invoice',
    done: 'done',
};

const Post = ({
    page,
    id,
    viewsCount,
    reviewsCount,
    rawPost,
    postStatus,
    publisherUid,
    date,
    title,
    description,
    budget,
    briefLink,
    category,
    subCategoryTitle,
    subCategories,
    invitationsPostIds,
    publishedPostIds = null,
}) => {
    const {
        currentUser: { uid, email, firstName: currentUserFirstName, lastName: currentUserLastName },
        isAdmin,
    } = useAuth();
    const [isOpenAddPortfolioPopup, setIsOpenAddPortfolioPopup] = useState(false);
    const [isOnlyReadBrief, setIsOnlyReadBrief] = useState(false);

    const handleToggleAddPortfolioPopup = useCallback(
        (onlyReadable = false) => {
            setIsOnlyReadBrief(onlyReadable);
            setIsOpenAddPortfolioPopup(prev => !prev);
        },
        [setIsOpenAddPortfolioPopup, setIsOnlyReadBrief]
    );

    const rawPostData = _cloneDeep(rawPost); // for update post in database
    const {
        removePostFromPendingsList,
        decreasePendingPostCount,
        decreaseInvitationsPostCount,
        increasePublicInvitations,
        changeStatusOfPublishedPost,
        changeStatusOfPublishedPostOnProjectPostPage,
        removeCompletedPost,
        deletePublishedPost,
        changePostAfterAnswer,
        changePostAfterAnswerOnProjectPostPage,
        fetchUserTeams,
    } = useActions();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpenModalConfirm, toggleIsOpenModalConfirm] = useToggle(false);

    useEffect(() => {
        fetchUserTeams();
    }, []);

    const postLink = `${window.location.origin}/posts/${id}`;

    const color = useMemo(() => SidekickCategories[category.toLowerCase()]?.color || '#30CC9D', [
        category,
    ]);

    const style = useMemo(() => {
        return {
            backgroundColor: color,
        };
    }, [color]);

    const isProjectPostPage = useMemo(() => location.pathname.includes(id), [location, id]);

    const wrapperClassname = classNames({
        'post-wrap': !isProjectPostPage,
        'project-post-wrap': isProjectPostPage,
    });

    const deletePostClassname = classNames({
        'post-delete-button': true,
        'project_page-post-delete-button': isProjectPostPage,
    });

    const postContentWrapperClassNames = classNames({
        'post-content-wrapper': !isProjectPostPage,
        'project-page__post-content-wrapper': isProjectPostPage,
    });

    const handleRequestInvoice = useCallback(async () => {
        rawPostData.status = postStatuses.requestInvoice;
        await updatePost(rawPostData).then(async () => {
            if (!isProjectPostPage) {
                changeStatusOfPublishedPost({ status: postStatuses.requestInvoice, id });
            } else {
                changeStatusOfPublishedPostOnProjectPostPage({
                    status: postStatuses.requestInvoice,
                });
            }
            const { email: senderEmail } = await getUserByUid(rawPostData.publisherUid);
            const recipientsInfo = rawPostData.assignTo;
            sendGridEmail(
                getInvoice(senderEmail, rawPostData.title, recipientsInfo, postLink, category)
            );
        });
    }, [
        rawPostData,
        postLink,
        category,
        id,
        changeStatusOfPublishedPost,
        changeStatusOfPublishedPostOnProjectPostPage,
        isProjectPostPage,
    ]);

    const handleCompleteProject = useCallback(async () => {
        rawPostData.status = postStatuses.done;
        await updatePost(rawPostData).then(async () => {
            if (!isProjectPostPage) {
                removeCompletedPost({ id });
            } else {
                navigate(ROUTES.MY_ALL_POSTS);
            }
            const { email: senderEmail } = await getUserByUid(rawPostData.publisherUid);
            const recipientsInfo = rawPostData.assignTo;
            sendGridEmail(getCompleteProject(senderEmail, recipientsInfo, postLink));
        });
    }, [rawPostData, postLink, id, removeCompletedPost, isProjectPostPage, navigate]);

    const handleResponseForInvite = useCallback(
        async ({ portfolioData = null, responseType }) => {
            const postOwnerData = await getUserByUid(publisherUid);
            const dataUser = await getUserByUid(uid).then(
                ({ firstName, lastName, email, position, website = '' }) => ({
                    firstName: firstName,
                    lastName: lastName,
                    fullName: firstName + ' ' + lastName, // rest for compatibility
                    email: email,
                    position: position,
                    website: website,
                })
            );

            const reviewDto = {
                donation: 0,
                minDonation: 0,
                postOwnerUid: publisherUid,
                postUid: id,
                publisherUid: uid,
                publisherFullName: dataUser.fullName, // rest for compatibility
                publisherFirstName: dataUser.firstName,
                publisherLastName: dataUser.lastName,
                publisherEmail: dataUser.email,
                publisherPosition: dataUser.position,
                publisherWebsite: dataUser.website,
                response: responseType,
            };

            if (portfolioData) reviewDto.portfolioData = { ...portfolioData };

            const review = await createReview(reviewDto);
            const actualPostData = await getPostByUid(id);

            if (!actualPostData.viewed.filter(item => item.email === email).length) {
                actualPostData.viewed.push({
                    name: dataUser.fullName, // rest for compatibility
                    firstName: dataUser.firstName,
                    lastName: dataUser.lastName,
                    email: email,
                    position: dataUser.position,
                    uid: uid,
                    website: dataUser.website,
                });
            }

            actualPostData.unviewed = actualPostData.unviewed.filter(item => item.email !== email);
            actualPostData.unreviewed = actualPostData.unreviewed.filter(
                item => item.email !== email
            );
            actualPostData.reviewIds.push(review.id);
            actualPostData.responseEmails.push({
                email,
                response: responseType,
                isPortfolioUploaded: !!portfolioData,
            });

            await updatePost(actualPostData).then(() => {
                markPostAsUnread(id);
                if (!isProjectPostPage) {
                    changePostAfterAnswer({ rawPostData: actualPostData, subCategoryTitle });
                } else {
                    changePostAfterAnswerOnProjectPostPage({
                        rawPostData: actualPostData,
                        newReview: reviewDto,
                    });
                }
                decreaseInvitationsPostCount({ id, subCategoryTitle });

                if (actualPostData.responseEmails.length === 1) {
                    // when first freelancer answer to invite
                    const recipientInfo = {
                        email: postOwnerData.email,
                        uid: postOwnerData.uid,
                        firstName: postOwnerData.firstName,
                        lastName: postOwnerData.lastName,
                    };
                    sendGridEmail(getFirstAnswerForInvite(email, recipientInfo, postLink, title));
                }
            });
        },
        [
            changePostAfterAnswer,
            changePostAfterAnswerOnProjectPostPage,
            decreaseInvitationsPostCount,
            email,
            isProjectPostPage,
            subCategoryTitle,
            id,
            postLink,
            publisherUid,
            title,
            uid,
        ]
    );

    const handleResponse = useCallback(
        async e => {
            e.stopPropagation();
            const responseType = e.target.getAttribute('data-type');
            switch (responseType) {
                case 'accept':
                    handleToggleAddPortfolioPopup();
                    break;
                case 'decline':
                    handleResponseForInvite({ responseType });
                    break;
                default:
                    break;
            }
        },
        [handleResponseForInvite, handleToggleAddPortfolioPopup]
    );

    const handleAcceptDeclineButtonClick = useCallback(
        async e => {
            const responseType = e.target.closest('.pending-button').getAttribute('data-type');

            switch (responseType) {
                case responsePendingTypes.decline:
                    rawPostData.isReviewedByAdmin = true;
                    rawPostData.isDeletedByAdmin = true;
                    rawPostData.rejectedByAdminDate = convertDateToFirestoreTimestamp(new Date());
                    break;
                case responsePendingTypes.accept:
                    rawPostData.isReviewedByAdmin = true;
                    rawPostData.isApprovedByAdmin = true;
                    rawPostData.approvedByAdminDate = convertDateToFirestoreTimestamp(new Date());
                    break;
                default:
                    break;
            }

            const postPublisherData = await getUserByUid(rawPostData.publisherUid);

            updatePost(rawPostData).then(() => {
                decreasePendingPostCount(id);
                removePostFromPendingsList({ rawPostData, responseType });
                if (
                    responseType === responsePendingTypes.accept &&
                    rawPostData.publisherUid !== uid
                ) {
                    increasePublicInvitations(rawPostData.uid);
                }

                if (isProjectPostPage) {
                    navigate(-1);
                }

                const recipientInfo = {
                    email: postPublisherData.email,
                    uid: postPublisherData.uid,
                    firstName: postPublisherData.firstName,
                    lastName: postPublisherData.lastName,
                };

                const parameterData =
                    responseType === responsePendingTypes.decline
                        ? getDeclinePost(email, recipientInfo, postLink)
                        : getAcceptPost(email, recipientInfo, postLink);

                sendGridEmail(parameterData);
            });
        },
        [
            id,
            rawPostData,
            decreasePendingPostCount,
            removePostFromPendingsList,
            email,
            uid,
            postLink,
            increasePublicInvitations,
            isProjectPostPage,
            navigate,
        ]
    );

    const handleCloseButtonClick = useCallback(
        e => {
            e.stopPropagation();
            toggleIsOpenModalConfirm();
        },
        [toggleIsOpenModalConfirm]
    );

    const handleConfirmCloseButtonClick = useCallback(async () => {
        rawPostData.isDeleted = true;

        await updatePost(rawPostData).then(async () => {
            if (!isProjectPostPage) {
                deletePublishedPost({ id });
            }
            toggleIsOpenModalConfirm();
            if (isProjectPostPage) {
                navigate(ROUTES.MY_ALL_POSTS);
            }
            if (rawPostData.assignTo) {
                const recipientsInfo = rawPostData.assignTo.map(assignedFreelancer => ({
                    email: assignedFreelancer.email,
                    uid: assignedFreelancer.uid,
                    firstName: assignedFreelancer.firstName,
                    lastName: assignedFreelancer.lastName,
                }));
                sendGridEmail(
                    getEmailsForHiredAfterDeletePost(
                        email,
                        title,
                        recipientsInfo,
                        currentUserFirstName,
                        currentUserLastName,
                        postLink,
                        category
                    )
                );
            } else if (!rawPostData.isPublicPost) {
                sendGridEmail(
                    getEmailsForInvitedAfterDeletePost(
                        email,
                        title,
                        rawPostData.inviteColleagues,
                        currentUserFirstName,
                        currentUserLastName,
                        postLink,
                        category
                    )
                );
            }
        });
    }, [
        rawPostData,
        isProjectPostPage,
        id,
        title,
        email,
        category,
        postLink,
        currentUserFirstName,
        currentUserLastName,
        deletePublishedPost,
        navigate,
        toggleIsOpenModalConfirm,
    ]);

    return (
        <div className={wrapperClassname} key={id}>
            <div style={style} className="post-progress-bar" />
            {(page === postsPages.published || (rawPost.isApprovedByAdmin && isAdmin)) && (
                <DeleteIcon
                    className={deletePostClassname}
                    title="delete post"
                    onClick={handleCloseButtonClick}
                />
            )}
            {!isProjectPostPage && (
                <PostHeader
                    postContentWrapperClassNames={postContentWrapperClassNames}
                    date={date}
                />
            )}
            <PostBody
                page={page}
                briefLink={briefLink}
                title={title}
                description={description}
                invitationsPostIds={invitationsPostIds}
                publishedPostIds={publishedPostIds}
                postId={id}
                email={email}
                responseEmails={rawPostData.responseEmails}
                isDeletedByAdmin={rawPostData.isDeletedByAdmin}
                isProjectPostPage={isProjectPostPage}
                postContentWrapperClassNames={postContentWrapperClassNames}
                openPortfolioPopup={handleToggleAddPortfolioPopup}
            />
            <PostFooter
                page={page}
                budget={budget}
                category={category}
                subCategoryTitle={subCategoryTitle}
                subCategories={subCategories}
                handleResponse={handleResponse}
                postStatus={postStatus}
                viewsCount={viewsCount}
                reviewsCount={reviewsCount}
                onAcceptDecline={handleAcceptDeclineButtonClick}
                requestInvoice={handleRequestInvoice}
                completeProject={handleCompleteProject}
                postContentWrapperClassNames={postContentWrapperClassNames}
                isProjectPostPage={isProjectPostPage}
                openPortfolioPopup={handleToggleAddPortfolioPopup}
                email={email}
                responseEmails={rawPostData.responseEmails}
                isDeletedByAdmin={rawPostData.isDeletedByAdmin}
                briefLink={briefLink}
            />
            {page === postsPages.published && isProjectPostPage && (
                <PublishedProposals color={color} />
            )}
            {isOpenModalConfirm && (
                <ConfirmationModal
                    renderIcon={() => <></>}
                    renderContent={() => <>Are you sure you want to delete this project?</>}
                    onConfirm={handleConfirmCloseButtonClick}
                    onCancel={handleCloseButtonClick}
                    onClickOutside={toggleIsOpenModalConfirm}
                />
            )}
            {isOpenAddPortfolioPopup && (
                <AddPortfolioToInvitePopup
                    postId={id}
                    postData={rawPostData}
                    isOpen={isOpenAddPortfolioPopup}
                    handleToggle={handleToggleAddPortfolioPopup}
                    handleResponseForInvite={handleResponseForInvite}
                    onlyReadableBrief={isOnlyReadBrief}
                    briefLink={briefLink}
                    isProjectPostPage={isProjectPostPage}
                    subCategoryTitle={subCategoryTitle}
                />
            )}
        </div>
    );
};

export default React.memo(Post);
